<template>
  <t-dropdown
    ref="dropdownRef"
    class="dropdown-MT"
    rounded-right
    no-border-left
    :label="label"
    @opened="focusQueryInput"
    @closed="closedDrop"
  >
    <template #selected-content>
      <p v-if="modelValue">
        {{ modelValue.label }}
      </p>
      <p v-else class="text-gray-100">
        {{ placeholder }}
      </p>
    </template>

    <template #content>
      <ul>
        <li
          class="border-0 border-b border-solid border-b-gray-400 !bg-gray-600"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.7812 15.6289C17.0547 15.9023 17.0547 16.3125 16.7812 16.5586C16.6719 16.6953 16.5078 16.75 16.3438 16.75C16.1523 16.75 15.9883 16.6953 15.8516 16.5586L12.1875 12.8945C11.2031 13.6875 9.97266 14.125 8.66016 14.125C5.54297 14.125 3 11.582 3 8.4375C3 5.32031 5.51562 2.75 8.66016 2.75C11.7773 2.75 14.3477 5.32031 14.3477 8.4375C14.3477 9.77734 13.9102 11.0078 13.1172 11.9648L16.7812 15.6289ZM4.3125 8.4375C4.3125 10.8711 6.25391 12.8125 8.6875 12.8125C11.0938 12.8125 13.0625 10.8711 13.0625 8.4375C13.0625 6.03125 11.0938 4.0625 8.6875 4.0625C6.25391 4.0625 4.3125 6.03125 4.3125 8.4375Z"
              fill="currentColor"
            />
          </svg>
          <input
            ref="queryInput"
            v-model="query"
            :placeholder="searchText"
            type="text"
            class="rounded-0 box-border block w-full border-none bg-gray-600 p-1 font-inter text-xs leading-[16px] text-gray-50 !outline-none transition duration-300 placeholder:text-gray-100 focus:!text-gray-10"
          />
        </li>
        <VueCustomScrollbar
          ref="MTScrollbar"
          style="position: relative; margin: auto"
          :style="{ height: calculatedHeight + 'px' }"
          :settings="{ wheelPropagation: false }"
        >
          <li
            v-for="(tag, k) in filteredMergeTags"
            :key="k"
            :ref="
              (el) => {
                refMT[k] = el;
              }
            "
            style="padding: 4px 0; display: flex; justify-content: flex-start"
            :class="{
              '!bg-gray-400': preselectTag === tag,
              '!bg-gray-600': preselectTag !== tag,
            }"
            @click="selectTag(tag)"
            @mouseover="preselectByMoseover(tag)"
          >
            <p
              style="width: 120px; padding-left: 12px"
              :class="{
                '!text-gray-50': preselectTag === tag,
                '!text-gray-100': preselectTag !== tag,
              }"
            >
              {{ tag.label }}
            </p>
          </li>
        </VueCustomScrollbar>
      </ul>
    </template>
  </t-dropdown>
</template>

<script lang="ts" setup>
  import {
    IMergeTag,
    ISmartMergeTag,
  } from "@/schemas/mergeTags/mergeTagsSchema";
  import { TDropdown } from "@topol/twig-ui";
  import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
  import { computed, ref, Ref, watch, onMounted, onBeforeUnmount } from "vue";

  type Props = {
    modelValue: IMergeTag | null;
    label?: string;
    disabled?: boolean;
    placeholder?: string;
    searchText?: string;
    width?: string;
    flatMergeTags?: IMergeTag[];
    smartMergeTags?: ISmartMergeTag;
    errorInput?: boolean;
    errorText?: string;
  };

  const props = withDefaults(defineProps<Props>(), {
    label: "",
    disabled: false,
    placeholder: "",
    searchText: "Search",
    width: "100%",
    flatMergeTags: () => [],
    smartMergeTags: () => ({
      enabled: true,
      syntax: { start: "*|", end: "|*" },
    }),
    errorInput: false,
    errorText: "",
  });

  const emits = defineEmits(["update:modelValue", "open-settings"]);

  const dropdownRef = ref();
  const query = ref("");
  const queryInput = ref();
  const refMT: Ref<any> = ref([]);
  const MTScrollbar = ref();
  const keyMove = ref(false);

  const filteredMergeTags = computed(() => {
    if (query.value === "") {
      return props.flatMergeTags;
    } else {
      return props.flatMergeTags.filter(
        (tag) =>
          tag.label.toLowerCase().includes(query.value.toLowerCase()) ||
          tag.value.toLowerCase().includes(query.value.toLowerCase())
      );
    }
  });

  const preselectTag = ref(
    props.flatMergeTags.find((tag) => tag === props.modelValue)
  );

  const calculatedHeight = computed(() => {
    const height = filteredMergeTags.value.length * 32;
    return height > 207 ? 207 : height;
  });

  function selectTag(tag: IMergeTag) {
    if (props.disabled) return;
    emits("update:modelValue", tag);
    dropdownRef.value.opened = false;
  }

  function focusQueryInput() {
    setTimeout(() => {
      query.value = "";
      queryInput.value.focus();
    }, 10);
  }

  function closedDrop() {
    query.value = "";
  }

  function preselectByMoseover(tag: IMergeTag) {
    if (keyMove.value) return;
    preselectTag.value = tag;
  }

  function keyEvents(event: KeyboardEvent) {
    if (!dropdownRef.value.opened || !preselectTag.value) return;
    keyMove.value = true;
    if (event.key === "ArrowDown") {
      const index = filteredMergeTags.value.indexOf(preselectTag.value);
      if (index < filteredMergeTags.value.length - 1) {
        preselectTag.value = filteredMergeTags.value[index + 1];
        scrollIntoView(index + 1);
      }
    } else if (event.key === "ArrowUp") {
      const index = filteredMergeTags.value.indexOf(preselectTag.value);
      if (index > 0) {
        preselectTag.value = filteredMergeTags.value[index - 1];
        scrollIntoView(index - 1);
      }
    } else if (event.key === "Enter") {
      selectTag(preselectTag.value);
    }
    setTimeout(() => {
      keyMove.value = false;
    }, 50);
  }

  function scrollIntoView(index: number) {
    const itemBottom =
      refMT.value[index].offsetTop + refMT.value[index].offsetHeight;
    const containerBottom =
      MTScrollbar.value.$el.scrollTop + MTScrollbar.value.$el.clientHeight;
    const itemTop = refMT.value[index].offsetTop;
    const containerTop = MTScrollbar.value.$el.scrollTop;
    if (itemBottom > containerBottom) {
      MTScrollbar.value.$el.scrollTop =
        itemBottom - MTScrollbar.value.$el.clientHeight;
    }
    if (itemTop < containerTop) {
      MTScrollbar.value.$el.scrollTop = itemTop;
    }
  }

  watch(filteredMergeTags, () => {
    if (query.value !== "") {
      preselectTag.value = filteredMergeTags.value[0];
    }
  });

  onMounted(() => {
    document.addEventListener("keydown", keyEvents);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", keyEvents);
  });
</script>
